/**
 * Lux Lightbox — shared full-screen viewer (images + hosted video).
 * - [data-lux-lightbox] host holds [data-lightbox-json] (array of
 *   { type:'image', src, thumb, alt } or { type:'video', poster, thumb, sources:[{url,mime}] }).
 * - Triggers: [data-lightbox-trigger="INDEX"] opens the gallery at INDEX.
 * - Video slides muted-autoplay when active, pause when scrolled away,
 *   with play/pause + mute/unmute controls (hosted <video> only).
 * - Also exposes a dedicated single-video popup via openVideoPopup().
 * No dependencies.
 */
(function () {
  'use strict';

  function init(host) {
    if (!host || host.dataset.lightboxReady) return;
    var json = host.querySelector('[data-lightbox-json]');
    if (!json) return;

    var items;
    try { items = JSON.parse(json.textContent); } catch (e) { return; }
    if (!items.length) return;

    host.dataset.lightboxReady = 'true';

    var opts = {
      thumbnails: host.dataset.lightboxThumbnails !== 'false',
      arrows: host.dataset.lightboxArrows !== 'false',
      counter: host.dataset.lightboxCounter !== 'false',
      backdrop: host.dataset.lightboxBackdrop !== 'false'
    };

    var overlay = buildOverlay(items, opts);
    document.body.appendChild(overlay);

    var stage = overlay.querySelector('[data-lightbox-stage]');
    var counter = overlay.querySelector('[data-lightbox-counter]');
    var thumbs = overlay.querySelectorAll('[data-lightbox-thumb]');
    var slides = overlay.querySelectorAll('[data-lightbox-slide]');
    var lastTrigger = null;

    function videoInSlide(i) {
      return slides[i] ? slides[i].querySelector('video') : null;
    }

    function pauseAllVideos() {
      overlay.querySelectorAll('video').forEach(function (v) { v.pause(); });
    }

    function playActiveVideo(index) {
      pauseAllVideos();
      var v = videoInSlide(index);
      if (v) {
        v.muted = true; // browsers require muted autoplay
        var p = v.play();
        if (p && p.catch) p.catch(function () {});
        syncVideoControls(v);
      }
      toggleVideoControls(index);
    }

    function setActive(index) {
      if (counter) counter.textContent = index + 1 + ' / ' + items.length;
      thumbs.forEach(function (t, i) { t.classList.toggle('is-active', i === index); });
      playActiveVideo(index);
    }

    function currentIndex() {
      if (!stage.clientWidth) return 0;
      return Math.round(stage.scrollLeft / stage.clientWidth);
    }

    function goTo(index, instant) {
      var clamped = Math.max(0, Math.min(items.length - 1, index));
      stage.scrollTo({ left: clamped * stage.clientWidth, behavior: instant ? 'auto' : 'smooth' });
      setActive(clamped);
    }

    function open(index, trigger) {
      lastTrigger = trigger || null;
      overlay.classList.add('is-open');
      document.body.classList.add('lux-lightbox-open');
      requestAnimationFrame(function () {
        goTo(index, true);
        overlay.querySelector('[data-lightbox-close]').focus();
      });
      document.addEventListener('keydown', onKey);
    }

    function close() {
      pauseAllVideos();
      overlay.classList.remove('is-open');
      document.body.classList.remove('lux-lightbox-open');
      document.removeEventListener('keydown', onKey);
      if (lastTrigger) lastTrigger.focus();
    }

    function onKey(event) {
      if (event.key === 'Escape') close();
      else if (event.key === 'ArrowRight') goTo(currentIndex() + 1);
      else if (event.key === 'ArrowLeft') goTo(currentIndex() - 1);
    }

    /* Video control buttons (in the top bar, shown only on video slides) */
    var playPauseBtn = overlay.querySelector('[data-lightbox-playpause]');
    var muteBtn = overlay.querySelector('[data-lightbox-mute]');

    function toggleVideoControls(index) {
      var isVideo = items[index] && items[index].type === 'video';
      if (playPauseBtn) playPauseBtn.hidden = !isVideo;
      if (muteBtn) muteBtn.hidden = !isVideo;
    }

    function syncVideoControls(v) {
      if (playPauseBtn) playPauseBtn.setAttribute('aria-pressed', v.paused ? 'false' : 'true');
      if (muteBtn) muteBtn.setAttribute('aria-pressed', v.muted ? 'false' : 'true');
    }

    if (playPauseBtn) {
      playPauseBtn.addEventListener('click', function () {
        var v = videoInSlide(currentIndex());
        if (!v) return;
        if (v.paused) { v.play(); } else { v.pause(); }
        syncVideoControls(v);
      });
    }
    if (muteBtn) {
      muteBtn.addEventListener('click', function () {
        var v = videoInSlide(currentIndex());
        if (!v) return;
        v.muted = !v.muted;
        syncVideoControls(v);
      });
    }

    overlay.querySelector('[data-lightbox-close]').addEventListener('click', close);

    var prev = overlay.querySelector('[data-lightbox-prev]');
    var next = overlay.querySelector('[data-lightbox-next]');
    if (prev) prev.addEventListener('click', function () { goTo(currentIndex() - 1); });
    if (next) next.addEventListener('click', function () { goTo(currentIndex() + 1); });

    if (opts.backdrop) {
      stage.addEventListener('click', function (event) {
        if (event.target === stage || event.target.classList.contains('lux-lightbox__slide')) close();
      });
    }

    thumbs.forEach(function (t, i) { t.addEventListener('click', function () { goTo(i); }); });

    var scrollT = null;
    stage.addEventListener('scroll', function () {
      clearTimeout(scrollT);
      scrollT = setTimeout(function () { setActive(currentIndex()); }, 80);
    }, { passive: true });

    /* Image/lightbox triggers on the host */
    host.addEventListener('click', function (event) {
      var trigger = event.target.closest('[data-lightbox-trigger]');
      if (!trigger || !host.contains(trigger)) return;
      event.preventDefault();
      open(parseInt(trigger.dataset.lightboxTrigger, 10) || 0, trigger);
    });

    /* Expose for the dedicated video popup: open lightbox at the first video */
    host._luxOpenVideo = function (trigger) {
      var idx = 0;
      for (var i = 0; i < items.length; i++) {
        if (items[i].type === 'video') { idx = i; break; }
      }
      open(idx, trigger);
    };
  }

  function buildOverlay(items, opts) {
    var overlay = document.createElement('div');
    overlay.className = 'lux-lightbox';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Media viewer');
    overlay.setAttribute('data-thumbnails', opts.thumbnails);
    overlay.setAttribute('data-arrows', opts.arrows);
    overlay.setAttribute('data-counter', opts.counter);

    var bar = document.createElement('div');
    bar.className = 'lux-lightbox__bar';

    if (opts.counter) {
      var counter = document.createElement('span');
      counter.className = 'lux-lightbox__counter';
      counter.setAttribute('data-lightbox-counter', '');
      counter.setAttribute('role', 'status');
      bar.appendChild(counter);
    }

    var controls = document.createElement('div');
    controls.className = 'lux-lightbox__vidcontrols';

    var playPause = document.createElement('button');
    playPause.type = 'button';
    playPause.className = 'lux-lightbox__vidbtn';
    playPause.setAttribute('data-lightbox-playpause', '');
    playPause.setAttribute('aria-label', 'Play or pause video');
    playPause.hidden = true;
    playPause.innerHTML =
      '<svg class="lux-lb-icon-pause" width="14" height="14" viewBox="0 0 14 14" aria-hidden="true"><rect x="2" y="1" width="3.5" height="12"/><rect x="8.5" y="1" width="3.5" height="12"/></svg>' +
      '<svg class="lux-lb-icon-play" width="14" height="14" viewBox="0 0 14 14" aria-hidden="true"><path d="M2 1l11 6-11 6z"/></svg>';

    var mute = document.createElement('button');
    mute.type = 'button';
    mute.className = 'lux-lightbox__vidbtn';
    mute.setAttribute('data-lightbox-mute', '');
    mute.setAttribute('aria-label', 'Mute or unmute video');
    mute.hidden = true;
    mute.innerHTML =
      '<svg class="lux-lb-icon-muted" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 9v6h4l5 4V5L8 9H4z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/><path d="M17 9l5 6M22 9l-5 6" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>' +
      '<svg class="lux-lb-icon-sound" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 9v6h4l5 4V5L8 9H4z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/><path d="M16.5 8.5a5 5 0 0 1 0 7M19 6a8.5 8.5 0 0 1 0 12" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';

    controls.appendChild(playPause);
    controls.appendChild(mute);
    bar.appendChild(controls);

    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'lux-lightbox__close';
    close.setAttribute('data-lightbox-close', '');
    close.setAttribute('aria-label', 'Close');
    close.innerHTML = '<svg width="16" height="16" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M1 1l12 12M13 1L1 13" stroke="currentColor" stroke-width="1.2"/></svg>';
    bar.appendChild(close);

    var stage = document.createElement('div');
    stage.className = 'lux-lightbox__stage';
    stage.setAttribute('data-lightbox-stage', '');

    items.forEach(function (item) {
      var slide = document.createElement('div');
      slide.className = 'lux-lightbox__slide';
      slide.setAttribute('data-lightbox-slide', '');

      if (item.type === 'video') {
        var video = document.createElement('video');
        video.className = 'lux-lightbox__video';
        video.setAttribute('playsinline', '');
        video.muted = true;
        video.loop = true;
        video.preload = 'metadata';
        if (item.poster) video.poster = item.poster;
        (item.sources || []).forEach(function (s) {
          var src = document.createElement('source');
          src.src = s.url;
          if (s.mime) src.type = s.mime;
          video.appendChild(src);
        });
        slide.appendChild(video);
      } else {
        var img = document.createElement('img');
        img.src = item.src;
        img.alt = item.alt || '';
        img.loading = 'lazy';
        img.decoding = 'async';
        slide.appendChild(img);
      }

      stage.appendChild(slide);
    });

    overlay.appendChild(bar);

    if (opts.arrows) {
      var prev = document.createElement('button');
      prev.type = 'button';
      prev.className = 'lux-lightbox__nav lux-lightbox__nav--prev';
      prev.setAttribute('data-lightbox-prev', '');
      prev.setAttribute('aria-label', 'Previous');
      prev.innerHTML = '<svg width="18" height="9" viewBox="0 0 20 10" fill="none" aria-hidden="true"><path d="M6 1L2 5l4 4M2 5h18" stroke="currentColor" stroke-width="1.2"/></svg>';
      overlay.appendChild(prev);
    }

    overlay.appendChild(stage);

    if (opts.arrows) {
      var next = document.createElement('button');
      next.type = 'button';
      next.className = 'lux-lightbox__nav lux-lightbox__nav--next';
      next.setAttribute('data-lightbox-next', '');
      next.setAttribute('aria-label', 'Next');
      next.innerHTML = '<svg width="18" height="9" viewBox="0 0 20 10" fill="none" aria-hidden="true"><path d="M14 1l4 4-4 4M18 5H0" stroke="currentColor" stroke-width="1.2"/></svg>';
      overlay.appendChild(next);
    }

    if (opts.thumbnails && items.length > 1) {
      var strip = document.createElement('div');
      strip.className = 'lux-lightbox__thumbs';
      items.forEach(function (item, i) {
        var t = document.createElement('button');
        t.type = 'button';
        t.className = 'lux-lightbox__thumb' + (i === 0 ? ' is-active' : '') + (item.type === 'video' ? ' lux-lightbox__thumb--video' : '');
        t.setAttribute('data-lightbox-thumb', i);
        t.setAttribute('aria-label', 'View item ' + (i + 1));
        t.innerHTML = '<img src="' + (item.thumb || item.src || item.poster || '') + '" alt="" loading="lazy">' +
          (item.type === 'video' ? '<span class="lux-lightbox__thumb-play" aria-hidden="true"><svg width="9" height="10" viewBox="0 0 16 18" fill="currentColor"><path d="M0 0l16 9-16 9z"/></svg></span>' : '');
        strip.appendChild(t);
      });
      overlay.appendChild(strip);
    }

    return overlay;
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-lux-lightbox]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) { initAll(event.target); });
})();